"use client";

import { useSearchParams } from "next/navigation";
import React, { RefObject, useEffect, useMemo, useState } from "react";

import { useTranslations } from "next-intl";
import { UseFormReturn } from "react-hook-form";
import { useMediaQuery } from "react-responsive";

import SearchLocationNew from "@/components/ui/SearchPanel/SearchModals/SearchLocationNew";
import { DealType } from "@/enums/dealType";
import { RegionResponse } from "@/services/search.service";
import { Filter } from "@/types/SearchFilter.types";
import { type AutocompleteItem } from "@/ui/Forms/AutocompleteList";
import FormSmartAutocomplete from "@/ui/Forms/FormSmartAutocomplete";
import { EMPTY_SLOT } from "@/utils/autocompleteBuildLocationOptions";
import { parseQueryToFilters } from "@/utils/parseQueryToFilters";

interface SearchLocationSmartProps {
  initialFilters: Filter;
  form: UseFormReturn<{ locationSearch: string }>;
  dealType: DealType;
  handleSearch: (
    filters?: Filter,
    type?: string,
    scrollToTop?: boolean,
  ) => void;
  regionsListData?: RegionResponse;
  watchedLocationSearch: string;
  onClosePopup: () => void;
  activePopUp: string;
  handleShowPopup: (ref: RefObject<HTMLElement | null>, name: string) => void;
  inputLocationRef: RefObject<HTMLInputElement | null>;
  groupSearchBlock: RefObject<HTMLDivElement | null>;
  locationOptions: {
    items: AutocompleteItem[];
    regions: string[];
  };
  onClear: () => void;
  localizedLocationString?: string;
}

const enum EnumPopUpName {
  location = "location",
}

const parseLocationValue = (value: string): Partial<Filter> => {
  const parts = value
    .split(",")
    .map((p) => p.trim())
    .filter(Boolean);
  const [region, city, district, microdistrict, street, metroStation] = parts;

  return {
    ...{ region: region ? [region] : [] },
    ...{ city: city && city !== EMPTY_SLOT ? [city] : [] },
    ...{ district: district && district !== EMPTY_SLOT ? [district] : [] },
    ...{
      microdistrict:
        microdistrict && microdistrict !== EMPTY_SLOT ? [microdistrict] : [],
    },
    ...{ street: street && street !== EMPTY_SLOT ? [street] : [] },
    ...{
      metroStation:
        metroStation && metroStation !== EMPTY_SLOT ? [metroStation] : [],
    },
  };
};

export default function SearchLocationSmart({
  initialFilters,
  form,
  dealType,
  handleSearch,
  regionsListData,
  watchedLocationSearch,
  onClosePopup,
  activePopUp,
  handleShowPopup,
  inputLocationRef,
  groupSearchBlock,
  locationOptions,
  onClear,
  localizedLocationString,
}: SearchLocationSmartProps) {
  const t = useTranslations();
  const searchParams = useSearchParams();
  const isMobile = useMediaQuery({
    query: "(max-width: 767px)",
  });
  const [selectedFilters, setSelectedFilters] =
    useState<Filter>(initialFilters);

  const locationValue = useMemo(() => {
    // If localizedLocationString is provided from parent (useUnifiedListing), use it
    if (localizedLocationString) {
      return localizedLocationString;
    }

    // Otherwise, parse from search params (existing behavior)
    const queryToFilters = parseQueryToFilters(searchParams, dealType);
    if (queryToFilters.region?.length) {
      return [
        queryToFilters?.region,
        queryToFilters?.city,
        queryToFilters?.district,
        queryToFilters?.microdistrict,
        queryToFilters?.street,
        queryToFilters?.metroStation,
      ]
        .flat()
        .filter(Boolean)
        .join(", ");
    }

    return "";
  }, [localizedLocationString, searchParams, dealType]);

  const handleSearchClick = (
    filters?: Filter,
    type?: string,
    scrollToTop?: boolean,
  ) => {
    handleSearch(filters, type, scrollToTop);
    onClosePopup();
  };

  useEffect(() => {
    setSelectedFilters(initialFilters);
  }, [initialFilters]);

  return (
    <FormSmartAutocomplete
      emptyBlock={
        <SearchLocationNew
          selectedFilters={selectedFilters}
          setSelectedFilters={setSelectedFilters}
          setSearchTitle={(value: string) => {
            form.setValue("locationSearch", value);
          }}
          dealType={dealType}
          handleSearch={handleSearchClick}
          regionsListData={regionsListData || []}
          searchTitleValue={watchedLocationSearch || locationValue}
          onClosePopup={onClosePopup}
          onSearchTitleChange={(e: React.FormEvent<HTMLInputElement>) => {
            const { value } = e.target as HTMLInputElement;
            form.setValue("locationSearch", value);
          }}
        />
      }
      listTitle={t("Enter location")}
      placeholder={t("Enter location")}
      options={locationOptions.items}
      value={locationValue}
      onChange={(value: string) => {
        form.setValue("locationSearch", value);
        const parsed = parseLocationValue(value);
        handleSearch({ ...initialFilters, ...parsed }, "location", false);
      }}
      activePopUp={activePopUp}
      isOpen={activePopUp === EnumPopUpName.location}
      onOpenChange={(isOpen: boolean) =>
        isOpen
          ? handleShowPopup(
              isMobile ? inputLocationRef : groupSearchBlock,
              EnumPopUpName.location,
            )
          : onClosePopup()
      }
      onClear={onClear}
      inputLocationRef={inputLocationRef}
    />
  );
}
